import React, { FC, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { Loader, Paragraph, Heading, Svg, Flex } from '@wunder-mobility/ui-components';
import {
  useRequestAvailableSubscriptionsQuery,
  useRequestMySubscriptionsQuery,
} from '@/features/subscriptions/services/Subscription.service';
import { useRequestResolvedBranchQuery } from '@/features/branches/services/Branches.service';
import {
  mergeSubscriptions,
  hasActiveOrExpiringSubscription,
} from '@/features/subscriptions/utils/subscriptionHelpers';
import SubscriptionTile from './SubscriptionTile';
import styles from '@/features/subscriptions/assets/scss/SubscriptionOverview.module.scss';

const SubscriptionOverview: FC = () => {
  const { data: resolvedBranch } = useRequestResolvedBranchQuery();

  // Fetch available subscriptions for the branch (catalog)
  const {
    data: availableSubscriptions,
    isLoading: isLoadingAvailable,
    error: errorAvailable,
  } = useRequestAvailableSubscriptionsQuery(
    { branchId: resolvedBranch?.id! },
    { skip: !resolvedBranch?.id },
  );

  // Fetch user's owned subscriptions
  const {
    data: mySubscriptions,
    isLoading: isLoadingMySubscriptions,
    error: errorMySubscriptions,
  } = useRequestMySubscriptionsQuery();

  const isLoading = isLoadingAvailable || isLoadingMySubscriptions;
  const error = errorAvailable || errorMySubscriptions;

  const allSubscriptions = useMemo(
    () => mergeSubscriptions(availableSubscriptions || [], mySubscriptions || []),
    [availableSubscriptions, mySubscriptions],
  );

  const hasActiveSubscription = useMemo(
    () => hasActiveOrExpiringSubscription(mySubscriptions || []),
    [mySubscriptions],
  );

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className={styles['subscription-overview__loading']}>
        <Loader />
      </Flex>
    );
  }

  if (error || !availableSubscriptions) {
    return (
      <Flex direction="column" align="center" gap="md">
        <Svg icon="info" width={60} />
        <Heading size={4}>
          <Trans id="subscriptions.error.title">Unable to load subscriptions</Trans>
        </Heading>
        <Paragraph>
          <Trans id="subscriptions.error.description">
            Please try again later or contact support.
          </Trans>
        </Paragraph>
      </Flex>
    );
  }

  if (allSubscriptions.length === 0) {
    return (
      <Flex direction="column" align="center" className={styles['subscription-overview__empty']}>
        <Flex className={styles['subscription-overview__empty-icon']}>
          <Svg icon="card" width={60} />
        </Flex>
        <Heading size={4} className={styles['subscription-overview__empty-title']}>
          <Trans id="subscriptions.empty.title">No subscriptions available</Trans>
        </Heading>
        <Paragraph className={styles['subscription-overview__empty-description']}>
          <Trans id="subscriptions.empty.description">
            There are currently no subscription plans available. Check back later!
          </Trans>
        </Paragraph>
      </Flex>
    );
  }

  return (
    <Flex direction="column" width="full" gap="xl">
      <Paragraph size={3} marginBottom={32}>
        <Trans id="subscriptions.overview.description">
          Choose a subscription plan that fits your needs. Enjoy exclusive benefits and savings on
          your rides.
        </Trans>
      </Paragraph>

      <Flex gap="lg" wrap width="full" className={styles['subscription-overview__tiles-container']}>
        {allSubscriptions.map((subscription) => (
          <SubscriptionTile
            key={subscription.code}
            subscription={subscription}
            hasActiveSubscription={hasActiveSubscription}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default SubscriptionOverview;
